import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';

import { AppModal } from 'src/components/AppModal';
import { AppForm } from 'src/components/AppForm';
import { locals } from 'src/constants/locals';
import { useTranslations } from 'src/hooks/translataions';
import {
  useEmbeddedContactForm, useStandardContactForm, useAttributesContactForm, useIsDevMode,
} from 'src/hooks/app';
import { IComplex, IGtmEventsTypes } from 'src/typings/complexes';
import { useBuildType, useIsMobile } from 'src/hooks';
import { isProdEnv } from 'src/helpers/buildEnv';
import { getCurrentMarketplace, getMarketplaceComplexDefaultConfig } from 'src/store/marketplace/selectors';
import { useSelector } from 'react-redux';
import { IMarketplacePlacement } from 'src/typings/marketplaceTile';
import { BookNowAside } from 'src/components/Marketplace/components/BookNowAside';
import { ButtonApp } from 'src/components/ApartmentPageV2/Button';
import { appTranslations } from 'src/constants/locals/appTranslation';
import CircularProgress from '@mui/material/CircularProgress';
import { useStyles as useFilterActionsStyles } from 'src/components/Marketplace/components/FilterActions/styles';
import Box from '@mui/material/Box';
import { getFormConfig } from 'src/store/requests/app/selector';
import { Index } from './TickDone';
import { getBookNowFormSchema } from './formSchema';
import { useSendForm } from './hooks';
import { useStyles } from './styles';

interface IModalBookNow {
  isOpen: boolean;
  onClose: () => void;
  complex?: Partial<IComplex> | null;
  placementData?: IMarketplacePlacement | null;
}

export const ModalBookNow: React.FC<IModalBookNow> = ({
  isOpen,
  onClose,
  complex,
  placementData,
}) => {
  const { isCrossTable, isMarketplace } = useBuildType();
  const styles = useStyles({});
  const formConfig = useSelector(getFormConfig);
  const { standardForm } = useStandardContactForm();
  const marketplaceStandardForm = useSelector(getMarketplaceComplexDefaultConfig);
  const bookNowFormSchema = getBookNowFormSchema({
    standardForm: marketplaceStandardForm?.client_module_standard_contact_form || standardForm,
    defaultCountry: marketplaceStandardForm?.form.form_default_country || formConfig?.form_default_country || 'ua',
  }, isMarketplace);
  const { getTranslations } = useTranslations();
  const { isDevMode } = useIsDevMode();
  const marketplace = useSelector(getCurrentMarketplace);
  const [needToSubmit, setNeedToSubmit] = useState<boolean>(false);
  const filterActionsClasses = useFilterActionsStyles();
  const {
    isSending,
    isDone,
    isError,
    handleSendForm,
  } = useSendForm();
  const { contactForm } = useEmbeddedContactForm();
  const { attributesForm } = useAttributesContactForm();
  const { isMobileScreen } = useIsMobile();

  const handleClick = useCallback((data: any) => {
    const formSubmitGTMEvent = complex?.frontConfig?.website?.gtm?.events && complex?.frontConfig.website.gtm.events.find((event) => event.type === IGtmEventsTypes.formSubmit);
    if (!isCrossTable && !isDevMode && isProdEnv() && window.dataLayer && formSubmitGTMEvent && typeof formSubmitGTMEvent.eventJson === 'object' && !Array.isArray(formSubmitGTMEvent.eventJson)) {
      console.log('GTM book now');
      window.dataLayer.push(formSubmitGTMEvent.eventJson);
    }
    handleSendForm(data, complex, placementData);
    setNeedToSubmit(false);
  }, [complex, placementData]);

  useEffect(() => {
    if (isDone) {
      setTimeout(onClose, 5000);
    }
  }, [isDone, onClose]);

  if (attributesForm) return null;

  if (contactForm && !standardForm) {
    const contactFormSrc = `<html lang="en"><head>
    <style>
      html, body { height: 100%; width: 100%; margin: 0; padding: 0}
      iframe { min-height: 100% }
    </style>
    </head><body>${contactForm.html}</body></html>`;
    return (
      <AppModal
        open={isOpen}
        onClose={onClose}
        toggleClose={onClose}
        contentHeader={<h3 style={{ textAlign: 'center', display: isDone ? 'none' : 'block' }}>{getTranslations(locals.appTranslations.appContactWithManager)}</h3>}
        listClassName={{
          wrapperContent: styles.wrapperPaddingIframe,
          wrapperContentHeader: styles.moduleWrapperContentHeader,
        }}
      >
        <iframe
          srcDoc={contactFormSrc}
          height="100%"
          width="100%"
          title="contact form"
          style={{
            border: 'none',
          }}
        />
      </AppModal>
    );
  }

  return (
    <AppModal
      open={isOpen}
      onClose={onClose}
      toggleClose={onClose}
      contentHeader={isDone ? null : <h3 style={{ textAlign: 'center', display: isDone ? 'none' : 'block' }}>{getTranslations(locals.appTranslations.appContactWithManager)}</h3>}
      listClassName={{
        wrapperContent: clsx(
          isMarketplace && styles.modalWrapperContent,
          !isMarketplace && styles.wrapperPadding,
          isDone && 'CustomWrapperDone',
        ),
        wrapperContentHeader: isMarketplace ? styles.modalWrapperContentHeader : styles.moduleWrapperContentHeader,
        customCloseButton: isMarketplace ? styles.modalCustomCloseButton : '',
      }}
    >
      <>
        {
          isDone
            ? (
              <>
                { !isMarketplace ? (
                  <div className={styles.isDoneWrapper}>
                    <Index />
                    <p className={styles.isDoneText}>
                      { getTranslations(locals.appTranslations.appPromiseCallbackToClient) }
                    </p>
                  </div>
                ) : (
                  <div className={`${styles.isDoneWrapper} ${styles.isDoneWrapperMarketplace}`}>
                    <img height="64px" src={marketplace?.developers[0].logoFileUri?.fileUrl} alt="fileUrl" />
                    <p className={styles.isDoneTextMarketplace}>{getTranslations(locals.appTranslations.appPromiseCallbackToClient)}</p>
                  </div>
                )}
              </>
            )
            : (
              <>
                {isMarketplace ? (
                  <>
                    <div className={styles.wrapper}>
                      <Box className={styles.section} display={isMobileScreen ? 'none' : 'block'}>
                        <BookNowAside />
                      </Box>
                      <div className={styles.section}>
                        <AppForm
                          isLoading={isSending}
                          schema={bookNowFormSchema}
                          handleSendForm={handleClick}
                          appendClassName="marketplace"
                          renderSubmitButton={false}
                          callSubmit={needToSubmit}
                        />
                      </div>
                    </div>
                    <div className={styles.termsAndConditionsWrapper}>
                      <span className={styles.termsAndConditions} />
                      <ButtonApp
                        variant="contained"
                        customStyles={`${filterActionsClasses.applyButton} ${styles.sendButton} `}
                        text={
                        isSending
                          ? <CircularProgress style={{ color: 'white' }} size="24px" />
                          : getTranslations(appTranslations.appSend)
                      }
                        onClick={() => setNeedToSubmit(true)}
                      />
                    </div>
                  </>
                ) : (
                  <AppForm
                    isLoading={isSending}
                    schema={bookNowFormSchema}
                    handleSendForm={handleClick}
                    appendClassName={styles.appendClassName}
                  />
                )}
              </>
            )
        }
        {isError.state && <p className={styles.errorTextMessage}>{isError.msg}</p>}
      </>
    </AppModal>
  );
};
